import { Component } from '@theme/component';

/** Progressive disclosure: prose remains fully readable without JavaScript. */
class HenryCollectionDescription extends Component {
  #observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (!entry) return;
    // Opening the prose changes its height, not its available width. Ignore
    // those notifications so our own visibility changes do not measure again.
    const width = entry.borderBoxSize?.[0]?.inlineSize ?? this.getBoundingClientRect().width;
    if (width !== this.#width) this.#scheduleMeasure();
  });
  #contentObserver = new MutationObserver((mutations) => {
    const { description, preview, toggle, label } = this.refs;
    const contentChanged = mutations.some((mutation) =>
      mutation.type === 'attributes'
        ? ['class', 'style', 'ref', 'data-more', 'data-less'].includes(mutation.attributeName ?? '')
        : !label?.contains(mutation.target)
    );
    // Some section morphs restore server-rendered hidden attributes without
    // calling updatedCallback on the retained component. Repair that state too,
    // but ignore our own writes once they match the cached disclosure state.
    const descriptionHidden = this.#collapsible && !this.#expanded;
    const stateChanged =
      description?.hidden !== descriptionHidden ||
      preview?.hidden !== !descriptionHidden ||
      toggle?.hidden !== !this.#collapsible ||
      toggle?.getAttribute('aria-expanded') !== String(this.#expanded) ||
      this.hasAttribute('data-expanded') !== this.#expanded;
    if (contentChanged || stateChanged) {
      super.updatedCallback();
      this.#scheduleMeasure();
    }
  });
  #frame = 0;
  #width = -1;
  #collapsible = false;
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#observeContent();
    // Listen before the first measurement. Already-loaded fonts are measured
    // now; current and subsequent font loads each invalidate through this event.
    document.fonts.addEventListener('loadingdone', this.#scheduleMeasure);
    this.#measure();
    this.#observer.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeContent();
    this.#scheduleMeasure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.#contentObserver.disconnect();
    document.fonts.removeEventListener('loadingdone', this.#scheduleMeasure);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #observeContent() {
    this.#contentObserver.disconnect();
    this.#contentObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-expanded', 'aria-expanded', 'class', 'style', 'ref', 'data-more', 'data-less'],
    });
  }

  #scheduleMeasure = () => {
    if (!this.isConnected || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#measure();
    });
  };

  #measure = () => {
    const { description, preview, toggle, label } = this.refs;
    if (!description || !preview || !toggle || !label) return;
    this.#width = this.getBoundingClientRect().width;
    // Measure without the toggle first, so its reserved width cannot cause a
    // short description to keep collapsing after the viewport grows.
    // All temporary visibility changes are restored before the browser paints.
    this.setAttribute('data-expanded', '');
    toggle.hidden = true;
    preview.hidden = true;
    description.hidden = false;
    const lineHeight = parseFloat(getComputedStyle(description).lineHeight);
    const multiline = description.scrollHeight > lineHeight + 1;
    description.hidden = true;
    preview.hidden = false;
    this.removeAttribute('data-expanded');
    this.#collapsible = multiline || preview.scrollWidth > preview.clientWidth + 1;
    if (!this.#collapsible) this.#expanded = false;
    this.#applyState();
  };

  #applyState() {
    const { description, preview, toggle, label } = this.refs;
    if (!description || !preview || !toggle || !label) return;
    toggle.hidden = !this.#collapsible;
    toggle.setAttribute('aria-expanded', String(this.#expanded));
    const text = this.#expanded ? toggle.dataset.less : toggle.dataset.more;
    if (label.textContent !== text) label.textContent = text;
    description.hidden = this.#collapsible && !this.#expanded;
    preview.hidden = !description.hidden;
    this.toggleAttribute('data-expanded', this.#expanded);
  }

  toggleDescription() {
    if (!this.#collapsible) return;
    this.#expanded = !this.#expanded;
    this.#applyState();
  }
}

if (!customElements.get('henry-collection-description')) {
  customElements.define('henry-collection-description', HenryCollectionDescription);
}
